import DesktopAwareNotification from "../core-components/DesktopAwareNotification";
import {Pencil} from 'lucide-react';
import React, {useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {collection, doc, getDoc, onSnapshot} from "firebase/firestore";
import {auth, db} from "../../Security/firebase-config";
import {eventSlots, flattenSetupEvent, hasSetupEventId, hasSetupMap, matchTitle, newSetupId, parseSetupKey, resolveSetupEvent, setupKey, setupRevision} from "../../utils/eventSetup";
import {ensureEventSetupIds, eventOwnerRef, saveEventSetup, saveSetupMap} from "../../utils/eventSetupStore";
import {IMPROVEMENT_NOTE_MAPS} from "../../utils/teamPlanning";
import {formatEventTime, getPlannerEvents} from "./plannerEvents";
import SessionEventPicker from "./SessionEventPicker";
import EventSetupFields from "./EventSetupFields";
import {withSetupTag} from "./eventSetupOptions";
import MatchLineup from "./MatchLineup";
import "./CalendarMatchLinker.css";

export default function EventSetup({teamId, selectedEventId = "", onSelectEvent, disabled = false, allowPersonal = false,
  mapId = "", mapName = "", onReadyChange, lockSelection = false, lifecycle, lineupSeed, lineupContainer, hideEventPicker = false, hideLineup = false,
  onAttachExisting, attachExistingLabel = "Attach existing stats/VOD", attachExistingTutorialId, requireMap = false}) {
  const initial = parseSetupKey(selectedEventId);
  const [scope, setScope] = useState(initial?.scope === "personal" && allowPersonal ? "personal" : "team");
  const [events, setEvents] = useState([]);
  const [maps, setMaps] = useState([]);
  const [members, setMembers] = useState([]);
  const [selection, setSelection] = useState(selectedEventId);
  const [draft, setDraft] = useState(null);
  const [base, setBase] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [pendingMap, setPendingMap] = useState("");
  const [lineupEditing, setLineupEditing] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [repairError, setRepairError] = useState(false);
  const [repairAttempt, setRepairAttempt] = useState(0);
  const callback = useRef(onSelectEvent);
  const readyCallback = useRef(onReadyChange);
  const lastTeam = useRef(teamId);
  const lastSelectionProp = useRef(selectedEventId);
  callback.current = onSelectEvent;
  readyCallback.current = onReadyChange;
  useEffect(() => {
    if (lastSelectionProp.current === selectedEventId) return;
    lastSelectionProp.current = selectedEventId;
    setSelection(selectedEventId || "");
    const identity = parseSetupKey(selectedEventId);
    if (identity?.scope === "personal" && allowPersonal) setScope("personal");
    else if (identity?.scope === "team") setScope("team");
  }, [selectedEventId, allowPersonal]);
  useEffect(() => {
    if (lastTeam.current !== teamId) {
      lastTeam.current = teamId; setSelection(""); setDraft(null); setBase(null); setMembers([]);
      callback.current?.(null, null); readyCallback.current?.(false);
    }
  }, [teamId]);
  useEffect(() => {
    if (scope === "personal" && !allowPersonal) {setScope("team"); setSelection(""); setDraft(null); callback.current?.(null, null);}
  }, [allowPersonal, scope]);
  useEffect(() => {
    setEvents([]); setError(""); setLoaded(false); setRepairing(false); setRepairError(false);
    if ((scope === "team" && !teamId) || !auth.currentUser) return undefined;
    let active = true, repairStarted = false, rosterVersion = 0, rosterSignature = null;
    const stop = onSnapshot(eventOwnerRef(teamId, scope), snapshot => {
      if (!active) return;
      const data = snapshot.data() || {};
      setLoaded(true);
      const calendarEvents = Array.isArray(data.events) ? data.events.filter(event => event && typeof event === "object" && !Array.isArray(event) && (event.calendar || scope) === scope) : [];
      setEvents(calendarEvents.filter(hasSetupEventId));
      if (calendarEvents.some(event => !hasSetupEventId(event)) && !repairStarted) {
        repairStarted = true; setRepairing(true);
        ensureEventSetupIds({teamId, scope}).catch(() => {
          if (active) setRepairError(true);
        }).finally(() => {if (active) setRepairing(false);});
      }
      const signature = JSON.stringify([data.members, data.teamRoles, data.memberRoles, data.roles, data.memberUsernames]);
      if (scope === "team" && signature !== rosterSignature) {
        rosterSignature = signature;
        const version = ++rosterVersion;
        Promise.all((Array.isArray(data.members) ? data.members : []).map(async member => {
        const uid = typeof member === "string" ? member : member?.uid;
        if (!uid) return null;
        const user = await getDoc(doc(db, "uids", uid)).catch(() => null);
        return {uid, username: user?.data()?.username || data.memberUsernames?.[uid] || member?.username || uid, role: data.teamRoles?.[uid] || data.memberRoles?.[uid] || data.roles?.[uid] || member?.role || "Player"};
      })).then(people => {if (active && version === rosterVersion) setMembers(people.filter(Boolean));});
      } else if (scope === "personal") setMembers([{uid: auth.currentUser.uid, username: data.username || auth.currentUser.displayName || "Me", role: "Player"}]);
    }, () => {if (!active) return; setLoaded(true); setEvents([]); setError("This calendar is unavailable. Check your membership and try again.");});
    return () => {active = false; stop();};
  }, [teamId, scope, repairAttempt]);
  useEffect(() => {
    if (!teamId) {setMaps([]); return undefined;}
    return onSnapshot(collection(db, "teams", teamId, "maps"), snap => setMaps(snap.docs.map(item => ({...item.data(), id: item.id}))), () => setMaps([]));
  }, [teamId]);
  const occurrences = useMemo(() => {
    // Use the shared recurrence expander for past, upcoming, deleted and moved occurrences.
    const teamEvents = events.map(event => ({...event, calendar: "team"}));
    if (hideEventPicker) {
      const event = resolveSetupEvent(teamEvents, selection);
      return event ? [{...event, calendar: scope}] : [];
    }
    return [...getPlannerEvents(teamEvents, {past: true}), ...getPlannerEvents(teamEvents)].filter(hasSetupEventId)
      .sort((a, b) => Date.parse(a.startUTC) - Date.parse(b.startUTC)).map(event => ({...event, calendar: scope}));
  }, [events, scope, hideEventPicker, selection]);
  const choices = useMemo(() => occurrences.flatMap(event => eventSlots(event).map(slot => ({
    key: setupKey(event, slot.id, scope), event, slot, label: `${formatEventTime(event).replace(" – ", " - ")} · ${matchTitle(event, slot)} · ${event.eventId.slice(-6)}`,
  }))), [occurrences, scope]);
  const selected = useMemo(() => choices.find(choice => choice.key === selection)
    || choices.find(choice => choice.event.eventId === selection && choice.slot.id === "map-1") || null, [choices, selection]);
  const missingMap = requireMap && !!selected && !hasSetupMap(selected.slot);
  useEffect(() => {setPendingMap("");}, [selected?.key, teamId]);
  useEffect(() => {
    if (disabled) return;
    if (draft || lineupEditing || !loaded || saving) {readyCallback.current?.(false); return;}
    readyCallback.current?.(!!selected && !missingMap);
    callback.current?.(selected?.key || null, selected ? flattenSetupEvent(selected.event, selected.slot, scope) : null);
  }, [selected, scope, draft, lineupEditing, loaded, disabled, missingMap, saving]);
  function createDraft() {
    const start = new Date(); start.setSeconds(0, 0);
    setSelection(""); callback.current?.(null, null); readyCallback.current?.(false);
    setBase(null); setError("");
    setDraft({eventId: newSetupId(), calendar: scope, tag: "", note: "", competition: "", opponentName: "", repeat: "none",
      startUTC: start.toISOString(), endUTC: new Date(+start + 3600000).toISOString(),
      participants: [], participantsUid: [], mapSlots: [{id: "map-1", mapId, mapName, titleOverride: ""}]});
  }
  function editSetup() {setBase(selected.event); setDraft(JSON.parse(JSON.stringify(selected.event))); setError("");}
  const stale = base && (!selected || setupRevision(base) !== setupRevision(selected.event));
  return <section className="EventSetup" aria-label="Setup">
    <div className="EventSetup__header">
      <h4>Setup</h4>
      {!draft && <div className="EventSetup__actions">
        {onAttachExisting && <button type="button" disabled={disabled || lineupEditing || !selected || !loaded} onClick={onAttachExisting} data-tutorial-id={attachExistingTutorialId}>{attachExistingLabel}</button>}
        {hideEventPicker && selected && <button type="button" aria-label="Edit shared setup" title="Edit shared setup" disabled={disabled || lineupEditing} onClick={editSetup}><Pencil size={16}/></button>}
      </div>}
    </div>
    {repairing && <p role="status">Preparing older calendar events…</p>}
    {repairError && <DesktopAwareNotification as="p" role="alert">Some older events could not be prepared. Your other events remain available. <button type="button" disabled={disabled} onClick={() => setRepairAttempt(value => value + 1)}>Retry loading older events</button></DesktopAwareNotification>}
    {allowPersonal && <label>Calendar<select disabled={disabled || saving || !!draft || lineupEditing} value={scope} onChange={e => {setScope(e.target.value); setSelection("");}}><option value="team">Team calendar</option><option value="personal">Personal calendar · local recording only</option></select></label>}
    {!hideEventPicker && <SessionEventPicker onEdit={selected && !draft ? editSetup : undefined} editDisabled={disabled || saving || !!draft || lineupEditing} value={selected?.key || ""} disabled={disabled || saving || !!draft || lineupEditing || lockSelection}
      options={[...choices.filter(choice => new Date(choice.event.endUTC || choice.event.startUTC) < new Date()).map(choice => ({value: choice.key, label: choice.label, group: "Past event"})),
        {value: "", label: "---------------- Please Select an Event ----------------", group: "Select an event to continue"},
        ...(!lockSelection ? [{value: "create-new-event", label: "Create a new event", group: "Create an event"}] : []),
        ...choices.filter(choice => new Date(choice.event.endUTC || choice.event.startUTC) >= new Date()).map(choice => ({value: choice.key, label: choice.label, group: "Upcoming event"}))]}
      onChange={key => {setError(""); if (key === "create-new-event") createDraft(); else setSelection(key);}}/>}
    {!draft && <span className="EventSetup__status">{selected ? "Saved setup · shared with Calendar, recordings and Team Stats" : "Select a saved event or create and save a new event before continuing."}</span>}
    {!draft && missingMap && <div className="EventSetup__required-map" role="group" aria-label="Select the event map">
      <div className="EventSetup__required-map-copy"><strong>Choose the map to continue</strong><p>{selected.slot.id.replace("map-", "Map ")} needs a map before you can import or record a VOD.</p></div>
      <label>Map<select value={pendingMap} disabled={disabled || saving} onChange={event => {setPendingMap(event.target.value); setError("");}}>
        <option value="">Select map</option>
        {Array.from(new Set([...IMPROVEMENT_NOTE_MAPS, ...maps.map(map => map.name || map.mapName || map.id)]))
          .filter(name => hasSetupMap({mapId: name})).map(name => <option key={name}>{name}</option>)}
      </select></label>
      <button className="EventSetup__save-map" type="button" disabled={!pendingMap || disabled || saving} onClick={async () => {
        setSaving(true); setError("");
        try {
          const map = maps.find(item => (item.name || item.mapName || item.id) === pendingMap);
          const saved = await saveSetupMap({teamId, key: selected.key, mapId: map?.id || pendingMap, mapName: pendingMap});
          lifecycle?.saved({teamId, scope, saved}, false);
        } catch (failure) {setError(failure.message || "Could not save the map. Try again.");}
        finally {setSaving(false);}
      }}>{saving ? "Saving map…" : "Save map"}</button>
    </div>}
    {!hideLineup && !draft && selected && (() => {
      const lineup = <MatchLineup teamId={teamId} selection={selected} members={members} disabled={disabled || saving} onEditingChange={setLineupEditing} seed={lineupSeed}/>;
      return lineupContainer ? createPortal(lineup, lineupContainer) : lineup;
    })()}
    {draft && <>
      <EventSetupFields value={draft} onChange={setDraft} maps={maps} members={members} disabled={saving || disabled}/>
      {stale && <DesktopAwareNotification as="p" role="alert">Another member changed this event. Reload the saved version to continue.</DesktopAwareNotification>}
      <div className="EventSetup__actions">
        <span className="EventSetup__status">Unsaved setup</span>
        <button className={'app-cancel-control'} type="button" disabled={saving} onClick={() => {setDraft(null); setBase(null); setError("");}}>Cancel</button>
        {base && <button type="button" disabled={saving || !selected} onClick={() => {setDraft(selected.event); setBase(selected.event); setError("");}}>Reload saved version</button>}
        <button type="button" disabled={saving || disabled || stale} onClick={async () => {
          setSaving(true); setError("");
          try {
            const saved = await saveEventSetup({teamId, scope, draft: withSetupTag(draft, draft.tag), previous: base});
            lifecycle?.saved({teamId, scope, saved}, !base);
            setSelection(setupKey(saved.repeat && saved.repeat !== "none" && !saved.instanceDate ? {...saved, instanceDate: saved.startUTC.slice(0, 10)} : saved, base && eventSlots(saved).some(slot => slot.id === selected?.slot.id) ? selected.slot.id : "map-1", scope));
            setDraft(null); setBase(null);
          }
          catch (failure) {setError(failure.message || "Could not save setup. Your draft is still here.");}
          finally {setSaving(false);}
        }}>{saving ? "Saving…" : "Save setup"}</button>
      </div>
    </>}
    {error && <DesktopAwareNotification as="p" role="alert">{error}</DesktopAwareNotification>}
  </section>;
}
